/*
 * Manful — native Gift Box add-on (replaces the "Super Gift Wrap & Gift Message" app).
 * Per-item: each CART line can be turned into a gift box for a flat +£X.
 *
 * CART ONLY, deliberately (2026-10-12). The product-page checkbox was removed after every
 * known free box (#4448 #4441 #4422 #4459 #4461 #4470 #4476) came from it. A PDP tick put the
 * "Gift box" property on the add-to-cart FORM, and Shopify's accelerated checkout
 * (<shopify-accelerated-checkout>, a closed shadow root) reads that form straight into a
 * checkout without ever touching the cart, so no charge line could exist. In the cart the
 * £3.95 is already a real line before any express button can be reached, so it rides to
 * checkout on its own. Do not reintroduce a PDP tick that sets a form property.
 *
 * Mechanism:
 *  - A boxed product line carries a visible line property "Gift box: Yes" (shows on the
 *    line + on the order for packing).
 *  - A single hidden "Gift Box" charge product is kept in sync: its quantity always equals
 *    the total number of gift-boxed units (recompute is the single source of truth).
 *
 * Theme contract (Impulse 8.0.0):
 *  - Impulse re-renders cart line items into [data-products]. We watch that container with a
 *    MutationObserver — every re-render (add / qty change / remove / drawer open) triggers us
 *    to (a) re-inject the per-line toggles and (b) reconcile the charge quantity. We do NOT
 *    rely on `cart:updated` alone — in Impulse it only fires after the theme's own mutations,
 *    not after a passive rebuild.
 *  - Our writes go to /cart/change.js and /cart/add.js, then we dispatch `cart:build` so
 *    Impulse re-renders. The observer is disconnected while we inject, so we never loop.
 *  Convergence: once the charge qty matches boxed units, recompute writes nothing.
 */
(function () {
  var CFG = window.MANFUL_GIFT_BOX || {};
  var VARIANT = Number(CFG.variantId);
  if (!VARIANT) { return; } // no Gift Box product resolved — do nothing

  var PROP_KEY = 'Gift box'; // no leading underscore => visible on line + order
  var PROP_VAL = 'Yes';
  // Never offer gift-boxing on these products: the gift-bundle merged line (its own whole-order
  // gift box handles it) and every add-on/charge product.
  var NO_GIFTBOX_HANDLES = { 'gift-bundle-standard': 1, 'gift-bundle-premium': 1, 'bundle-engraving': 1, 'bundle-gift-box': 1, 'engraving': 1, 'gift-box': 1 };
  var LABEL = CFG.label || 'Make it a gift box';
  var PRICE_TEXT = CFG.priceText || '';
  var ROUTES = (window.theme && window.theme.routes) || {};
  var URL_CART = ROUTES.cart || '/cart.js';
  var URL_CHANGE = ROUTES.cartChange || '/cart/change.js';
  var URL_ADD = ROUTES.cartAdd || '/cart/add.js';
  var URL_UPDATE = ROUTES.cartUpdate || '/cart/update.js';

  var syncing = false;   // a recompute is in flight
  var pending = false;   // another recompute was requested mid-flight — re-run when current finishes
  var busy = false;      // a line-split (setLineBoxQty) is in flight — ignore overlapping toggles (prevents the add-more-while-boxed race)
  var injecting = false; // true while we mutate the DOM (so the observer ignores our own edits)
  var observers = [];
  var debounceTimer = null;
  var syncLockTimer = null;

  // A boxed line reaches the cart BEFORE its charge line does: measured live 2026-09-21, the
  // drawer's checkout button was clickable at 538ms and the £3.95 line landed at 1210ms, a
  // ~670ms window in which the cart holds "Gift box: Yes" and no charge. Order #4461 went out
  // through exactly that gap. So freeze every checkout entry point while a reconcile is in
  // flight. The class sits on <body> (the drawer is outside the product form) and drives an
  // !important rule in gift-box-addon.liquid, so it survives Impulse re-rendering the drawer.
  // SYNC_LOCK_MAX_MS is the safety valve: a sync that never settles must never be able to hold
  // checkout shut, so the lock always expires on its own.
  // Forensic stamp. Writes "<boxed units>/<charge qty>" to the cart attribute `_gb_sync`, which
  // rides onto the order. If a gift box ever ships free again the ORDER ITSELF says which half
  // failed: "1/0" = the reconcile had not converged when this cart was checked out, "1/1" = it
  // had, so the charge was lost somewhere else entirely and the cause is NOT this engine.
  // Order #4461 cost a day of inference because nothing recorded this. Cart attributes can be
  // rewritten freely, unlike line-item properties which need a remove + re-add, so this costs no
  // churn and cannot disturb the lines themselves. Underscore prefix keeps it out of the
  // customer's view, matching the gift-bundle engine's own `_bundle_map` attributes.
  var lastStamp = null;
  var stampChain = Promise.resolve();
  function stamp(want, have) {
    var v = want + '/' + have;
    if (v === lastStamp) { return stampChain; }                                 // only write on change
    if (want === 0 && have === 0 && lastStamp === null) { return stampChain; }  // never touch a box-free cart
    lastStamp = v;
    // SERIALISED, never concurrent. Two un-chained /cart/update.js calls can land out of order,
    // and on 2026-10-12 a "1/0" issued BEFORE the charge landed arrived AFTER the "1/1" issued
    // once it had, leaving a fully charged cart stamped as uncharged (reproduced on demand by
    // delaying the 1/0 write 1.5s). Each write now starts only when the previous one finished,
    // so the last stamp issued is always the last one stored.
    stampChain = stampChain.then(function () {
      return post(URL_UPDATE, { attributes: { _gb_sync: v } });
    }).catch(function () { lastStamp = null; });
    return stampChain;
  }

  var SYNC_LOCK_MAX_MS = 5000;
  function setSyncing(on) {
    clearTimeout(syncLockTimer);
    if (!document.body) { return; }
    document.body.classList.toggle('mf-giftbox-syncing', !!on);
    if (on) {
      syncLockTimer = setTimeout(function () {
        document.body.classList.remove('mf-giftbox-syncing');
        if (window.console) { console.warn('[giftbox] sync lock expired after ' + SYNC_LOCK_MAX_MS + 'ms'); }
      }, SYNC_LOCK_MAX_MS);
    }
  }

  // Temporary on-screen debug panel (enabled via CFG.debug) — lets us see the remove flow
  // without driving the browser. Logs to a fixed panel on <body> that survives cart re-renders.
  function mfLog(m) {
    if (!CFG.debug) { return; }
    var p = document.getElementById('mf-debug');
    if (!p) {
      p = document.createElement('div');
      p.id = 'mf-debug';
      p.style.cssText = 'position:fixed;bottom:0;left:0;z-index:999999;background:#000;color:#3f3;font:11px/1.4 monospace;max-height:45vh;overflow:auto;padding:6px 8px;max-width:96vw;white-space:pre-wrap;border-top-right-radius:6px';
      document.body.appendChild(p);
    }
    p.textContent += m + '\n';
    p.scrollTop = p.scrollHeight;
    if (window.console) { console.log('[giftbox]', m); }
  }

  function getCart() {
    return fetch(URL_CART + (URL_CART.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now(), {
      headers: { 'Accept': 'application/json' }, credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  var rebuildTimer = null;
  function rebuild() {
    // Coalesce the multiple rebuild requests in one user action into a SINGLE cart re-render,
    // so the drawer doesn't visibly redraw 2-3 times per click.
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(function () { document.dispatchEvent(new CustomEvent('cart:build')); }, 60);
  }

  function boxedUnits(cart) {
    var n = 0;
    (cart.items || []).forEach(function (it) {
      if (it.variant_id === VARIANT) { return; } // never count the charge line itself
      if (it.properties && it.properties[PROP_KEY] === PROP_VAL) { n += it.quantity; }
    });
    return n;
  }

  function chargeLine(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === VARIANT) { return items[i]; }
    }
    return null;
  }

  // Single source of truth: charge qty must equal boxed units. Writes only on mismatch.
  function recompute() {
    if (syncing) { pending = true; return Promise.resolve(false); } // coalesce — re-run after current
    syncing = true;
    return getCart().then(function (cart) {
      var want = boxedUnits(cart);
      var line = chargeLine(cart);
      var have = line ? line.quantity : 0;
      mfLog('recompute: want=' + want + ' have=' + have);
      stamp(want, have); // record the state a checkout would snapshot RIGHT NOW
      if (want === have) { return false; }
      setSyncing(true); // cart and charge disagree — hold checkout until they agree again
      // change.js must target the line by its KEY (variant-id alone returns 400 on this store);
      // add.js is correct by variant id when no charge line exists yet.
      var req = (want > 0 && !line)
        ? post(URL_ADD, { items: [{ id: VARIANT, quantity: want }] })
        : post(URL_CHANGE, { id: line.key, quantity: want });
      // Wait for the converged stamp to LAND before resolving: recompute resolving is what
      // releases the checkout lock, so this keeps checkout shut until the order would carry
      // the true "n/n" rather than the transient "n/0".
      return req.then(function () { return stamp(want, want); }).then(function () { return true; });
    }).then(function (changed) {
      syncing = false;
      if (changed) { rebuild(); }
      if (pending) { pending = false; return recompute(); } // a change happened mid-run — reconcile again
      setSyncing(false); // charge qty now matches boxed units — checkout is safe
      return changed;
    }).catch(function (e) {
      syncing = false;
      if (window.console) { console.warn('[giftbox] recompute failed', e); }
      if (pending) { pending = false; return recompute(); }
      setSyncing(false); // never strand checkout behind a failed reconcile
      return false;
    });
  }

  // Set how many of a line's units are gift-boxed (0..lineQty). Splits the line into a boxed
  // sub-line (qty n) + a plain sub-line (qty lineQty-n), carrying any other properties to both.
  // change.js canNOT add/remove properties, so we remove the line and re-add the piece(s).
  function setLineBoxQty(key, n) {
    if (busy) { return Promise.resolve(); } // serialize splits — an overlapping one would race the cart and could thrash
    busy = true;
    setSyncing(true); // the line is about to be removed and re-added — no checkout mid-split
    return getCart().then(function (cart) {
      var item = null;
      (cart.items || []).forEach(function (it) { if (it.key === key) { item = it; } });
      if (!item) { return; }
      var q = item.quantity;
      n = Math.max(0, Math.min(n, q));
      var base = {};
      if (item.properties) { for (var k in item.properties) { if (item.properties.hasOwnProperty(k) && k !== PROP_KEY) { base[k] = item.properties[k]; } } }
      var sp = (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) ? item.selling_plan_allocation.selling_plan.id : null;
      function mk(qty, boxed) {
        if (qty <= 0) { return null; }
        var props = {};
        for (var k2 in base) { if (base.hasOwnProperty(k2)) { props[k2] = base[k2]; } }
        if (boxed) { props[PROP_KEY] = PROP_VAL; }
        var ai = { id: item.variant_id, quantity: qty };
        if (Object.keys(props).length) { ai.properties = props; }
        if (sp) { ai.selling_plan = sp; }
        return ai;
      }
      var adds = [mk(q - n, false), mk(n, true)].filter(Boolean);
      return post(URL_CHANGE, { id: key, quantity: 0 })      // remove the original line
        .then(function () {
          var chain = Promise.resolve();
          adds.forEach(function (ai) { chain = chain.then(function () { return post(URL_ADD, { items: [ai] }); }); });
          return chain; // re-add boxed + plain pieces
        })
        .then(function () { rebuild(); return recompute(); }); // refresh, then sync charge qty
    }).then(function (r) { busy = false; return r; }, function (e) {
      busy = false; if (window.console) { console.warn('[giftbox] setLineBoxQty failed', e); }
    });
  }

  // Remove ALL gift boxes: un-tag every boxed line (remove + re-add without the prop), then
  // reconcile — which drops the charge line to zero. Used by the charge line's "Remove" link.
  function removeAllGiftBoxes() {
    mfLog('removeAllGiftBoxes() called');
    return getCart().then(function (cart) {
      var boxed = (cart.items || []).filter(function (it) {
        return it.variant_id !== VARIANT && it.properties && it.properties[PROP_KEY] === PROP_VAL;
      });
      mfLog('boxed lines found: ' + boxed.length);
      var chain = Promise.resolve();
      boxed.forEach(function (it) {
        chain = chain.then(function () {
          var props = {};
          for (var k in it.properties) { if (it.properties.hasOwnProperty(k) && k !== PROP_KEY) { props[k] = it.properties[k]; } }
          var addItem = { id: it.variant_id, quantity: it.quantity };
          if (Object.keys(props).length) { addItem.properties = props; }
          if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
            addItem.selling_plan = it.selling_plan_allocation.selling_plan.id;
          }
          return post(URL_CHANGE, { id: it.key, quantity: 0 })
            .then(function (r) { mfLog('  remove resp items=' + (r && r.item_count)); return post(URL_ADD, { items: [addItem] }); })
            .then(function (r) { mfLog('  re-add resp: ' + (r && (r.id ? 'ok' : (r.description || JSON.stringify(r).slice(0, 80))))); });
        });
      });
      return chain.then(function () {
        return getCart().then(function (c2) {
          var bu = boxedUnits(c2); var cl = chargeLine(c2);
          mfLog('after untag: boxedUnits=' + bu + ' chargeQty=' + (cl ? cl.quantity : 0));
          rebuild(); return recompute();
        });
      });
    });
  }

  function toggleMarkup(checked) {
    return '<label class="mf-giftbox__label">' +
      '<input type="checkbox" class="mf-giftbox__cb"' + (checked ? ' checked' : '') + '>' +
      '<span class="mf-giftbox__text">' + LABEL +
      (PRICE_TEXT ? ' <span class="mf-giftbox__price">(+' + PRICE_TEXT + ')</span>' : '') +
      '</span></label>';
  }

  function optionsHtml(q, cur) {
    var opts = '';
    for (var i = 0; i <= q; i++) { opts += '<option value="' + i + '"' + (i === cur ? ' selected' : '') + '>' + i + '</option>'; }
    return opts;
  }

  // Quantity picker for lines with qty > 1: "Gift boxes: [N] of Q (+£X each)".
  function selectMarkup(q, cur) {
    var opts = optionsHtml(q, cur);
    return '<label class="mf-giftbox__label">' +
      '<span class="mf-giftbox__text">' + (CFG.qtyLabel || 'Gift boxes') + ':</span> ' +
      '<select class="mf-giftbox__sel">' + opts + '</select> ' +
      '<span class="mf-giftbox__text">of ' + q +
      (PRICE_TEXT ? ' <span class="mf-giftbox__price">(+' + PRICE_TEXT + ' each)</span>' : '') +
      '</span></label>';
  }

  // Re-inject per-line toggles. Idempotent: updates existing checkboxes, adds where missing.
  function injectCartToggles(cart) {
    var byKey = {};
    (cart.items || []).forEach(function (it) { byKey[it.key] = it; });

    var nodes = document.querySelectorAll('.cart__item[data-key]');
    Array.prototype.forEach.call(nodes, function (node) {
      var key = node.getAttribute('data-key');
      var it = byKey[key];
      if (!it) { return; }

      if (it.variant_id === VARIANT) { // the charge line — hidden entirely; managed only via the
        node.style.display = 'none';   // per-product checkbox/dropdown. Qty stays in the cart total.
        return;
      }

      // Never offer gift-boxing on non-shippable fee lines (engraving, bundle engraving, the
      // whole-bundle gift box) or on any line that is part of a gift bundle — the bundle has its
      // own whole-order gift-box option, and boxing a fee line makes no sense.
      var bp = it.properties || {};
      if (it.requires_shipping === false || bp._bundle_id || bp._bundle_engraving || bp._giftbox) { return; }
      if (it.handle && NO_GIFTBOX_HANDLES[it.handle]) { return; }

      // Hide the theme's auto-rendered "Gift box: Yes" property line in the cart — our control
      // already shows the state. (Still stored on the line + visible on the order for packing.)
      Array.prototype.forEach.call(node.querySelectorAll('.cart__item--properties'), function (pr) {
        var lbl = pr.querySelector('span');
        if (lbl && lbl.textContent.replace(/:\s*$/, '').trim() === PROP_KEY) { pr.style.display = 'none'; }
      });

      var qy = it.quantity;
      var boxed = !!(it.properties && it.properties[PROP_KEY] === PROP_VAL);
      var curN = boxed ? qy : 0;
      var wantType = (qy === 1) ? 'cb' : 'sel';
      var existing = node.querySelector('.mf-giftbox');
      // Update in place when the control type already matches — no destroy/recreate, no flicker.
      if (existing && existing.getAttribute('data-mf-type') === wantType) {
        if (wantType === 'cb') {
          var cbX = existing.querySelector('.mf-giftbox__cb');
          if (cbX) { cbX.checked = boxed; cbX.disabled = false; }
        } else {
          var selX = existing.querySelector('.mf-giftbox__sel');
          if (selX) {
            if (selX.options.length !== qy + 1) { selX.innerHTML = optionsHtml(qy, curN); }
            selX.value = String(curN); selX.disabled = false;
          }
        }
        return;
      }
      if (existing) { existing.parentNode.removeChild(existing); } // type changed (qty crossed 1)
      var wrap = document.createElement('div');
      wrap.className = 'mf-giftbox';
      wrap.setAttribute('data-mf-type', wantType);
      (node.querySelector('.cart__item-details') || node).appendChild(wrap);
      if (qy === 1) {
        wrap.innerHTML = toggleMarkup(boxed);
        wrap.querySelector('.mf-giftbox__cb').addEventListener('change', function () {
          this.disabled = true; // re-enabled on next rebuild
          setLineBoxQty(key, this.checked ? 1 : 0);
        });
      } else {
        wrap.innerHTML = selectMarkup(qy, curN);
        wrap.querySelector('.mf-giftbox__sel').addEventListener('change', function () {
          this.disabled = true;
          setLineBoxQty(key, Number(this.value));
        });
      }
    });
  }

  function disconnectObservers() { observers.forEach(function (o) { o.disconnect(); }); }
  function reconnectObservers() {
    observers.forEach(function (o) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-products]'), function (el) {
        o.observe(el, { childList: true, subtree: true });
      });
    });
  }

  // INJECT-ONLY pass. Runs on every cart re-render to keep toggles present + charge line
  // locked. NEVER writes to the cart — that would create a write/observe feedback loop.
  function injectPass() {
    if (injecting) { return; }
    getCart().then(function (cart) {
      injecting = true;
      disconnectObservers();
      injectCartToggles(cart);
      injecting = false;
      reconnectObservers();
    });
  }

  function scheduleInject() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(injectPass, 150);
  }

  function startObserving() {
    var targets = document.querySelectorAll('[data-products]');
    if (!targets.length) { return; }
    // Observer ONLY re-injects toggles. Reconciliation is event-driven (below), so there is
    // no path where an observed mutation triggers a cart write.
    var mo = new MutationObserver(function () { if (!injecting) { scheduleInject(); } });
    observers.push(mo);
    Array.prototype.forEach.call(targets, function (el) {
      mo.observe(el, { childList: true, subtree: true });
    });
  }

  // Reconciliation is driven ONLY by user-initiated cart changes:
  //  - ajaxProduct:added → Impulse fires THIS, and not cart:updated, after a PDP ajax add.
  //    Listening to cart:updated alone is why a PDP gift-box tick reached checkout carrying
  //    "Gift box: Yes" but no £3.95 charge line: nothing reconciled before the customer left
  //    the page, and it only self-healed if they happened to load another store page (init()
  //    calls recompute). engraving-cart.js has always listened to both events, which is why
  //    the engraving fee never had this bug. Verified on the live store 2026-09-16.
  //  - cart:updated  → Impulse fired it after the customer changed qty / removed a line.
  //  - toggle click  → handled in setLineBoxQty (which calls recompute itself).
  // recompute()'s own writes use raw fetch (no events), so this never loops.
  document.addEventListener('ajaxProduct:added', function () { recompute(); });
  document.addEventListener('cart:updated', function () { recompute(); });

  function init() {
    startObserving();
    injectPass();
    recompute(); // reconcile any pre-existing boxed lines on load
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
